// Rascunho NÃO SALVO do Orçamento (Rafael, 08/10/2026) — liga a tela ao rascunho local + servidor.
//
// Fases: 'carregando' (até a tela terminar de carregar e assentar os recálculos) → 'verificando'
// (lê rascunho do navegador e do servidor) → 'perguntando' (modal Restaurar/Descartar) → 'pronto'.
// Só em 'pronto' (e fora de recarga) a tela pode ficar "suja" e só então há autosave.
// Base ("o que está salvo") = assinatura da tela; acompanha os recálculos automáticos e só para de
// acompanhar quando a mudança vem logo depois de clique/tecla do operador (decidirEstadoRascunho).
//
// Parte 3 (Rafael, 16/10 — feita 19/10):
// - servidor a cada 15 s, só se mudou desde o último envio que deu certo;
// - erro no envio vira aviso na tela + nova tentativa em 30 s e depois 60 s;
// - durante o Salvar nada de rascunho sai (antesDeSalvar/fimDoSalvar); resposta de envio que saiu
//   antes de um Salvar/Descartar não mexe na tela e, se recriou o rascunho, ele é apagado; o servidor
//   também ignora esse envio atrasado (sessão + seq da tela, ver OrcamentoRascunhoPolicy no back);
// - sair pela navegação do sistema abre o aviso (history.block) em vez do confirm do navegador.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    INTERVALO_RASCUNHO_SERVIDOR_MS,
    MENSAGEM_SAIR_SEM_SALVAR,
    acaoAposEnvioAtrasado,
    assinaturaRascunho,
    buildChave,
    decidirEstadoRascunho,
    escolherRascunhoMaisRecente,
    esperaNovaTentativaMs,
    montarPayloadRascunho,
    novaSessaoTela,
    rascunhoDesatualizado,
} from './rascunhoOrcamentoPolicy';
import {
    buscarRascunhosDeOutros,
    buscarServidor,
    carregarLocal,
    limparLocal,
    limparServidor,
    limparTudo,
    salvarLocal,
    salvarServidor,
    salvarServidorNaSaida,
} from '../../util/rascunhoOrcamento';

const ATRASO_ASSINATURA_MS = 300;
const INTERVALO_LOCAL_MS = 2000;
const ASSENTAR_APOS_CARGA_MS = 1200;
const ASSENTAR_MAXIMO_MS = 8000;
const EVENTOS_INTERACAO = ['input', 'change', 'paste', 'cut', 'drop', 'focusout', 'keydown', 'pointerdown'];
const SELETOR_EDITAVEL = 'input, textarea, select, [contenteditable="true"]';
// Clique que pode mudar dado: botão, link, campo, opção de select/menu. Rolar a grade (barra, área
// livre) não conta — senão um recálculo automático atrasado logo depois viraria "alteração".
const SELETOR_CLICAVEL = 'button, a, label, input, textarea, select, [role], [contenteditable="true"], '
    + '[class*="option"], [class*="Option"], [class*="select"], [class*="Select"], [class*="dropdown"]';

const elementoDoEvento = (evento) => (evento.target && evento.target.nodeType === 1 ? evento.target : null);
const casa = (elemento, seletor) => {
    try { return !!(elemento && elemento.closest && elemento.closest(seletor)); } catch (e) { return false; }
};

function ehInteracaoDeEdicao(evento) {
    if (evento.isTrusted === false) return false;
    const alvo = elementoDoEvento(evento);
    switch (evento.type) {
        case 'pointerdown':
            return casa(alvo, SELETOR_CLICAVEL);
        case 'keydown':
            // tecla em campo, ou Enter/Espaço acionando botão; setas na grade (rolagem) não contam
            return casa(alvo, SELETOR_EDITAVEL) || evento.key === 'Enter' || evento.key === ' ';
        case 'focusout':
            return casa(alvo, SELETOR_EDITAVEL);
        default:
            return true;
    }
}

export default function useRascunhoOrcamento({
    usuario, token, id, copiar, dados, colunasNegociadoAdicionais, carregado, carregando, aplicar,
}) {
    const [sujo, setSujo] = useState(false);
    const [guardadoEm, setGuardadoEm] = useState(null);
    const [pendente, setPendente] = useState(null);
    const [versaoFase, setVersaoFase] = useState(0);
    // { status, semConexao, esperaMs, tentando } enquanto o envio ao servidor estiver falhando
    const [erroServidor, setErroServidor] = useState(null);
    // navegação interna barrada por alteração não salva: { location, action }
    const [saida, setSaida] = useState(null);
    // D-0052 parte 4a: [{ nome, salvo_em }] de outros operadores com alteração não salva neste orçamento
    const [outrosOperadores, setOutrosOperadores] = useState([]);
    const history = useHistory();

    const faseRef = useRef('carregando');
    const chaveRef = useRef(null);
    const baselineRef = useRef(null);
    const sujoRef = useRef(false);
    const ultimaInteracaoRef = useRef(0);
    const dadosRef = useRef(dados);
    const colunasRef = useRef(colunasNegociadoAdicionais);
    const timerLocalRef = useRef(null);
    const timerServidorRef = useRef(null);
    const enviadoServidorRef = useRef(null);
    const gravadoLocalRef = useRef(null);
    const verificacaoIniciadaRef = useRef(false);
    const montadoRef = useRef(true);
    // 11/10: mudança que o servidor já gravou (câmbio do topo pelo modal) — a tela atual vira a base.
    const aceitarRef = useRef(false);
    // Parte 3: sessão desta tela + sequência dos envios (o servidor ignora envio anterior a um Salvar dela).
    const sessaoRef = useRef(novaSessaoTela());
    const seqRef = useRef(0);
    // Muda a cada Salvar/Descartar/aceitar: resposta de envio de antes disso não mexe na tela.
    const geracaoRef = useRef(0);
    const bloqueadoRef = useRef(false);
    const emVooRef = useRef(false);
    const reenviarAposVooRef = useRef(false);
    const falhasRef = useRef(0);
    const liberarSaidaRef = useRef(false);
    dadosRef.current = dados;
    colunasRef.current = colunasNegociadoAdicionais;

    const mudarFase = (fase) => {
        faseRef.current = fase;
        setVersaoFase((v) => v + 1);
    };

    const marcarSujo = (valor) => {
        sujoRef.current = valor;
        setSujo(valor);
    };

    const payloadAtual = () => {
        try {
            return JSON.stringify(montarPayloadRascunho(dadosRef.current, colunasRef.current));
        } catch (e) {
            return null;
        }
    };
    const baseAtualizadoEm = () => (dadosRef.current && dadosRef.current.updated_at) || null;

    const gravarLocalAgora = useCallback(async () => {
        if (!sujoRef.current || !chaveRef.current) return;
        const payload = payloadAtual();
        if (!payload || payload === gravadoLocalRef.current) return;
        const chave = chaveRef.current;
        const geracao = geracaoRef.current;
        const salvoEm = new Date().toISOString();
        const ok = await salvarLocal(usuario, chave, { payload, salvo_em: salvoEm, base_atualizado_em: baseAtualizadoEm() });
        if (geracao !== geracaoRef.current) {
            // Salvou/descartou enquanto gravava: a cópia do navegador ficou velha.
            if (ok && (!sujoRef.current || chave !== chaveRef.current)) limparLocal(usuario, chave);
            return;
        }
        if (ok) {
            gravadoLocalRef.current = payload;
            if (montadoRef.current && sujoRef.current) setGuardadoEm(salvoEm);
        }
    }, [usuario]); // eslint-disable-line react-hooks/exhaustive-deps

    const limparErroServidor = () => {
        falhasRef.current = 0;
        if (montadoRef.current) setErroServidor(null);
    };

    const enviarServidorAgoraRef = useRef(null);
    const agendarServidor = (esperaMs, substituir = false) => {
        if (!montadoRef.current) return;
        if (timerServidorRef.current) {
            if (!substituir) return;
            window.clearTimeout(timerServidorRef.current);
        }
        timerServidorRef.current = window.setTimeout(() => {
            timerServidorRef.current = null;
            if (enviarServidorAgoraRef.current) enviarServidorAgoraRef.current();
        }, esperaMs);
    };

    // Intervalo (não debounce): digitação contínua grava no máximo a cada 2 s / 15 s, com o dado do momento.
    // Com erro pendente, o timer do servidor já é o da nova tentativa (30/60 s) e não é trocado.
    const agendarAutosave = () => {
        if (!timerLocalRef.current) {
            timerLocalRef.current = window.setTimeout(() => { timerLocalRef.current = null; gravarLocalAgora(); }, INTERVALO_LOCAL_MS);
        }
        agendarServidor(INTERVALO_RASCUNHO_SERVIDOR_MS);
    };

    const enviarServidorAgora = useCallback(async () => {
        if (!sujoRef.current || !chaveRef.current || bloqueadoRef.current) return;
        if (emVooRef.current) {
            reenviarAposVooRef.current = true;
            return;
        }
        const payload = payloadAtual();
        if (!payload || payload === enviadoServidorRef.current) return;
        const chave = chaveRef.current;
        const geracao = geracaoRef.current;
        seqRef.current += 1;
        const marca = { sessao: sessaoRef.current, seq: seqRef.current };
        emVooRef.current = true;
        if (falhasRef.current > 0 && montadoRef.current) setErroServidor((e) => (e ? { ...e, tentando: true } : e));
        const resultado = await salvarServidor(token, chave, payload, baseAtualizadoEm(), marca);
        emVooRef.current = false;
        if (geracao !== geracaoRef.current) {
            // Saiu antes de um Salvar/Descartar e voltou depois: não mexe na tela; se o servidor gravou
            // (chegou antes do Salvar ou numa corrida), o rascunho velho some ou é trocado pelo atual.
            reenviarAposVooRef.current = false;
            if (resultado.ok && !resultado.ignorado) {
                const acao = acaoAposEnvioAtrasado({ chaveEnvio: chave, chaveAtual: chaveRef.current, sujo: sujoRef.current });
                if (acao === 'apagar') limparServidor(token, chave);
                if (acao === 'reenviar') {
                    enviadoServidorRef.current = null;
                    agendarAutosave();
                }
            }
            return;
        }
        if (resultado.ok) {
            if (!resultado.ignorado) enviadoServidorRef.current = payload;
            limparErroServidor();
            if (montadoRef.current && sujoRef.current && resultado.salvoEm) setGuardadoEm(resultado.salvoEm);
            if (reenviarAposVooRef.current) {
                reenviarAposVooRef.current = false;
                agendarAutosave();
            }
            return;
        }
        // Rafael 16/10: erro é avisado e tenta de novo em 30 s, depois a cada 60 s.
        reenviarAposVooRef.current = false;
        falhasRef.current += 1;
        const esperaMs = esperaNovaTentativaMs(falhasRef.current);
        if (montadoRef.current) {
            setErroServidor({ status: resultado.status, semConexao: !!resultado.semConexao, esperaMs, tentando: false });
        }
        agendarServidor(esperaMs, true);
    }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
    enviarServidorAgoraRef.current = enviarServidorAgora;

    const cancelarTimers = () => {
        if (timerLocalRef.current) window.clearTimeout(timerLocalRef.current);
        if (timerServidorRef.current) window.clearTimeout(timerServidorRef.current);
        timerLocalRef.current = null;
        timerServidorRef.current = null;
    };

    /** Grava já (local + servidor). `saindo` = a página está fechando: usa keepalive no servidor. */
    const flush = useCallback((saindo = false) => {
        if (!sujoRef.current || !chaveRef.current) return;
        cancelarTimers();
        gravarLocalAgora();
        // Salvar em andamento: nenhum rascunho sai (ele chegaria depois e recriaria o que foi salvo).
        if (bloqueadoRef.current) return;
        if (saindo) {
            const payload = payloadAtual();
            if (payload && payload !== enviadoServidorRef.current) {
                seqRef.current += 1;
                salvarServidorNaSaida(token, chaveRef.current, payload, baseAtualizadoEm(), { sessao: sessaoRef.current, seq: seqRef.current });
                enviadoServidorRef.current = payload;
            }
        } else {
            enviarServidorAgora();
        }
    }, [token, gravarLocalAgora, enviarServidorAgora]); // eslint-disable-line react-hooks/exhaustive-deps

    // Interação do operador (clique/tecla/digitação), em captura no documento inteiro (modais em portal).
    useEffect(() => {
        const registrar = (evento) => { if (ehInteracaoDeEdicao(evento)) ultimaInteracaoRef.current = Date.now(); };
        EVENTOS_INTERACAO.forEach((tipo) => document.addEventListener(tipo, registrar, true));
        return () => EVENTOS_INTERACAO.forEach((tipo) => document.removeEventListener(tipo, registrar, true));
    }, []);

    // Sujo ou não, a cada mudança (com um respiro para não serializar a cada tecla).
    useEffect(() => {
        const timer = window.setTimeout(() => {
            let assinatura;
            try {
                assinatura = assinaturaRascunho(montarPayloadRascunho(dadosRef.current, colunasRef.current));
            } catch (e) {
                return;
            }
            if (aceitarRef.current) {
                aceitarRef.current = false;
                baselineRef.current = assinatura;
                if (sujoRef.current) marcarSujo(false);
                return;
            }
            const resultado = decidirEstadoRascunho({
                pronto: faseRef.current === 'pronto' && !carregando,
                sujo: sujoRef.current,
                assinatura,
                baseline: baselineRef.current,
                ultimaInteracaoEm: ultimaInteracaoRef.current,
                agora: Date.now(),
            });
            baselineRef.current = resultado.baseline;
            if (resultado.sujo !== sujoRef.current) marcarSujo(resultado.sujo);
            if (resultado.sujo) agendarAutosave();
            // Voltou a ficar igual ao salvo: não há o que proteger, o aviso de erro sai.
            else if (falhasRef.current > 0) {
                if (timerServidorRef.current) window.clearTimeout(timerServidorRef.current);
                timerServidorRef.current = null;
                limparErroServidor();
            }
        }, ATRASO_ASSINATURA_MS);
        return () => window.clearTimeout(timer);
    }, [dados, colunasNegociadoAdicionais, carregando, versaoFase]); // eslint-disable-line react-hooks/exhaustive-deps

    const verificarRascunho = useCallback(async () => {
        if (verificacaoIniciadaRef.current) return;
        verificacaoIniciadaRef.current = true;
        mudarFase('verificando');
        const chave = buildChave({ eventoId: dadosRef.current && dadosRef.current.evento, orcamentoId: id, copiar });
        chaveRef.current = chave;
        // D-0052 parte 4a (Rafael, 23/10): outro operador com alteração não salva neste orçamento? Só ao
        // abrir e só em orçamento gravado; não segura a oferta de restaurar o próprio rascunho.
        if (id && !copiar) {
            Promise.resolve(buscarRascunhosDeOutros(token, chave)).then((lista) => {
                if (montadoRef.current && Array.isArray(lista)) setOutrosOperadores(lista);
            });
        }
        const [local, servidor] = await Promise.all([carregarLocal(usuario, chave), buscarServidor(token, chave)]);
        if (!montadoRef.current) return;
        const escolhido = escolherRascunhoMaisRecente(local, servidor);
        if (!escolhido) {
            mudarFase('pronto');
            return;
        }
        let payload = null;
        try { payload = JSON.parse(escolhido.payload); } catch (e) { payload = null; }
        let atual = null;
        try { atual = assinaturaRascunho(montarPayloadRascunho(dadosRef.current, colunasRef.current)); } catch (e) { atual = null; }
        if (!payload || !payload.dados || assinaturaRascunho(payload) === atual) {
            // Rascunho ilegível ou igual ao que já está salvo: some sem perguntar.
            limparTudo(usuario, token, chave);
            mudarFase('pronto');
            return;
        }
        setPendente({
            payload,
            salvo_em: escolhido.salvo_em,
            desatualizado: rascunhoDesatualizado(escolhido.base_atualizado_em, baseAtualizadoEm()),
        });
        mudarFase('perguntando');
    }, [usuario, token, id, copiar]); // eslint-disable-line react-hooks/exhaustive-deps

    // Depois da carga, espera os recálculos assentarem (1,2 s sem mudança; no máximo 8 s) e confere rascunho.
    const inicioAssentarRef = useRef(null);
    useEffect(() => {
        if (!carregado || carregando || verificacaoIniciadaRef.current) return undefined;
        if (!inicioAssentarRef.current) inicioAssentarRef.current = Date.now();
        const restante = Math.max(0, ASSENTAR_MAXIMO_MS - (Date.now() - inicioAssentarRef.current));
        const timer = window.setTimeout(verificarRascunho, Math.min(ASSENTAR_APOS_CARGA_MS, restante));
        return () => window.clearTimeout(timer);
    }, [carregado, carregando, dados, colunasNegociadoAdicionais, verificarRascunho]);

    // Depois de um POST a rota troca para /cadastroOrcamento/<novo> sem remontar: a chave acompanha.
    useEffect(() => {
        if (faseRef.current === 'pronto' && chaveRef.current) {
            chaveRef.current = buildChave({ eventoId: dadosRef.current && dadosRef.current.evento, orcamentoId: id, copiar });
        }
    }, [id, copiar]);

    const restaurar = useCallback(() => {
        if (!pendente) return;
        const { payload } = pendente;
        setPendente(null);
        aplicar(payload);
        marcarSujo(true);
        setGuardadoEm(pendente.salvo_em || null);
        mudarFase('pronto');
    }, [pendente, aplicar]);

    const descartar = useCallback(() => {
        const chave = chaveRef.current;
        geracaoRef.current += 1;
        setPendente(null);
        if (chave) limparTudo(usuario, token, chave);
        mudarFase('pronto');
    }, [usuario, token]);

    /** Chamar quando o orçamento foi salvo com sucesso: rascunho some e a base é o que está na tela. */
    const limparAposSalvar = useCallback(() => {
        cancelarTimers();
        geracaoRef.current += 1;
        bloqueadoRef.current = false;
        reenviarAposVooRef.current = false;
        limparErroServidor();
        const chave = chaveRef.current;
        marcarSujo(false);
        setGuardadoEm(null);
        ultimaInteracaoRef.current = 0;
        baselineRef.current = null;
        gravadoLocalRef.current = null;
        enviadoServidorRef.current = null;
        if (chave) limparTudo(usuario, token, chave);
        setVersaoFase((v) => v + 1);
    }, [usuario, token]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * O servidor já gravou a mudança que está na tela (ex.: "Salvar Câmbio" do modal, que grava o câmbio
     * do topo e o Fechamento das linhas). Só chamar quando NÃO havia outra alteração pendente antes:
     * a tela atual vira a base e o rascunho criado por essa mudança some (não é "alteração não salva").
     */
    const aceitarEstadoAtualComoSalvo = useCallback(() => {
        cancelarTimers();
        geracaoRef.current += 1;
        reenviarAposVooRef.current = false;
        limparErroServidor();
        aceitarRef.current = true;
        marcarSujo(false);
        setGuardadoEm(null);
        ultimaInteracaoRef.current = 0;
        gravadoLocalRef.current = null;
        enviadoServidorRef.current = null;
        const chave = chaveRef.current;
        if (chave) limparTudo(usuario, token, chave);
        setVersaoFase((v) => v + 1);
    }, [usuario, token]); // eslint-disable-line react-hooks/exhaustive-deps

    // Saída: aba/janela some ou recarrega.
    useEffect(() => {
        const aoOcultar = () => { if (document.visibilityState === 'hidden') flush(false); };
        const aoSairDaPagina = () => flush(true);
        const antesDeDescarregar = (evento) => {
            if (!sujoRef.current) return undefined;
            flush(true);
            evento.preventDefault();
            evento.returnValue = MENSAGEM_SAIR_SEM_SALVAR;
            return MENSAGEM_SAIR_SEM_SALVAR;
        };
        document.addEventListener('visibilitychange', aoOcultar);
        window.addEventListener('pagehide', aoSairDaPagina);
        window.addEventListener('beforeunload', antesDeDescarregar);
        return () => {
            document.removeEventListener('visibilitychange', aoOcultar);
            window.removeEventListener('pagehide', aoSairDaPagina);
            window.removeEventListener('beforeunload', antesDeDescarregar);
        };
    }, [flush]);

    // Desmontou (navegou para outra tela dentro do sistema): grava o que tiver.
    useEffect(() => {
        montadoRef.current = true;
        return () => {
            montadoRef.current = false;
            flush(false);
            cancelarTimers();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Saída pela navegação do sistema (menu, voltar, links) com alteração não salva: barra e abre o
    // aviso da tela (sairSemSalvar / ficarNaTela). O rascunho é enviado na hora.
    useEffect(() => {
        if (!history || typeof history.block !== 'function') return undefined;
        const desbloquear = history.block((location, action) => {
            if (liberarSaidaRef.current || !sujoRef.current) return undefined;
            flush(false);
            setSaida({ location, action });
            return false;
        });
        return desbloquear;
    }, [history, flush]);

    const sairSemSalvar = useCallback(() => {
        const destino = saida;
        setSaida(null);
        if (!destino || !destino.location) return;
        liberarSaidaRef.current = true;
        try {
            if (destino.action === 'REPLACE') history.replace(destino.location);
            else history.push(destino.location);
        } finally {
            liberarSaidaRef.current = false;
        }
    }, [saida, history]);

    const ficarNaTela = useCallback(() => setSaida(null), []);

    /**
     * D-0052 parte 4b: recarrega a página depois do 409 (outra pessoa salvou), sem o aviso de saída do
     * navegador. O rascunho vai antes (navegador + servidor); ao abrir de novo, a tela oferece
     * Restaurar/Descartar, avisando que o orçamento foi salvo depois do rascunho.
     */
    const recarregarTela = useCallback(() => {
        flush(true);
        sujoRef.current = false;
        window.location.reload();
    }, [flush]); // eslint-disable-line react-hooks/exhaustive-deps

    /** Início do Salvar: nenhum rascunho sai até ele terminar (o envio chegaria depois e recriaria o rascunho). */
    const antesDeSalvar = useCallback(() => {
        bloqueadoRef.current = true;
        if (timerServidorRef.current) window.clearTimeout(timerServidorRef.current);
        timerServidorRef.current = null;
    }, []);

    /** Fim do Salvar (deu certo ou não). Se ainda há alteração (erro, validação), o rascunho volta a sair. */
    const fimDoSalvar = useCallback(() => {
        bloqueadoRef.current = false;
        if (sujoRef.current) agendarServidor(falhasRef.current > 0 ? esperaNovaTentativaMs(falhasRef.current) : INTERVALO_RASCUNHO_SERVIDOR_MS);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /** Marca da tela para a chave de salvamento ("<sessão>_<último seq>"). Chamar depois de antesDeSalvar. */
    const marcaSalvar = useCallback(() => `${sessaoRef.current}_${seqRef.current}`, []);

    return {
        sujo,
        guardadoEm,
        pendente,
        erroServidor,
        saida,
        outrosOperadores,
        restaurar,
        descartar,
        limparAposSalvar,
        aceitarEstadoAtualComoSalvo,
        antesDeSalvar,
        fimDoSalvar,
        marcaSalvar,
        sairSemSalvar,
        ficarNaTela,
        recarregarTela,
        mensagemSairSemSalvar: MENSAGEM_SAIR_SEM_SALVAR,
        chaveAtual: () => chaveRef.current,
    };
}
